// client/src/pages/BlogAdmin.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  'Clinical Research', 'Drug Development', 'Patient Advocacy', 'Regulatory',
  'Public Health', 'Technology', 'News',
  'Precision Medicine', 'Biostatistics & Data', 'Rare Diseases',
  'Oncology Trials', 'Neuroscience Trials', 'Pediatric Research',
  "Women's Health", 'Global Health', 'Digital Health',
  'Ethics & Compliance', 'Funding & Grants', 'Career & Education'
];

const STATUS_STYLE = {
  published: { background: '#dcfce7', color: '#15803d' },
  draft: { background: 'var(--paper-2)', color: 'var(--muted)' },
};

const emptyForm = {
  title: '', slug: '', excerpt: '', content: '', metaTitle: '',
  metaDescription: '', category: 'Clinical Research', tags: '',
  author: 'BioMedArticles Editorial', status: 'draft', image: '',
  imageAlt: '', seoScore: 0, aiGenerated: false, featured: false
};

export default function BlogAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // list | editor | ai-topics | ai-editor
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState('');

  // AI states
  const [aiTopics, setAiTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);

  // Auto-generation states
  const [autoGenStatus, setAutoGenStatus] = useState(null);
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [autoGenTriggering, setAutoGenTriggering] = useState(false);
  const [previewTopics, setPreviewTopics] = useState(null);
  const [autoGenCount, setAutoGenCount] = useState(3);

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchBlogs();
    fetchAutoGenStatus();
  }, []); // eslint-disable-line

  const fetchAutoGenStatus = async () => {
    try {
      const { data } = await axios.get('/api/blog/admin/autogen/status');
      setAutoGenStatus(data);
    } catch {}
  };

  const handleAutoGenTrigger = async () => {
    if (!window.confirm(`Generate ${autoGenCount} new blog drafts now? This will use AI credits.`)) return;
    setAutoGenTriggering(true);
    try {
      const { data } = await axios.post('/api/blog/admin/autogen/trigger', { count: autoGenCount });
      if (data.success) {
        const gen = data.result.generated?.length || 0;
        const failed = data.result.failed?.length || 0;
        setMsg(`Auto-generated ${gen} drafts${failed > 0 ? ` (${failed} failed)` : ''}. Review them below.`);
        fetchBlogs();
        fetchAutoGenStatus();
      }
    } catch (err) {
      setMsg('Auto-generation failed: ' + (err.response?.data?.message || err.message));
    }
    setAutoGenTriggering(false);
  };

  const handleToggleAutoCron = async () => {
    try {
      const newEnabled = !autoGenStatus?.enabled;
      await axios.put('/api/blog/admin/autogen/config', {
        enabled: newEnabled,
        postsPerDay: autoGenCount
      });
      fetchAutoGenStatus();
      setMsg(newEnabled ? 'Auto-generation enabled — 3 drafts daily at 6 AM ET' : 'Auto-generation disabled');
    } catch {}
  };

  const handlePreviewTopics = async () => {
    setAutoGenLoading(true);
    try {
      const { data } = await axios.get(`/api/blog/admin/autogen/preview?count=${autoGenCount}`);
      setPreviewTopics(data.topics);
    } catch {}
    setAutoGenLoading(false);
  };

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/blog/admin/all');
      setBlogs(data.blogs || []);
    } catch {}
    setLoading(false);
  };

  const handleSuggestTopics = async () => {
    setLoadingTopics(true);
    setAiTopics([]);
    try {
      const { data } = await axios.post('/api/blog/admin/suggest-topics');
      setAiTopics(data.topics || []);
      setView('ai-topics');
    } catch (err) {
      setMsg('Failed to get topic suggestions. Check your ANTHROPIC_API_KEY.');
    }
    setLoadingTopics(false);
  };

  const handleGenerateFromTopic = async (topic) => {
    setSelectedTopic(topic);
    setLoadingGenerate(true);
    setView('ai-editor');
    try {
      const { data } = await axios.post('/api/blog/admin/generate', {
        title: topic.title,
        category: topic.category,
        keywords: topic.keywords
      });
      const generated = data.blog;
      setForm({
        ...emptyForm,
        ...generated,
        tags: Array.isArray(generated.tags) ? generated.tags.join(', ') : '',
        category: topic.category,
        aiGenerated: true,
        status: 'draft'
      });
      setEditId(null);
    } catch (err) {
      setMsg('Failed to generate blog. Try again.');
      setView('ai-topics');
    }
    setLoadingGenerate(false);
  };

  const handleSave = async (publishNow = false) => {
    if (!form.title || !form.content || !form.excerpt) {
      setMsg('Title, excerpt, and content are required.');
      return;
    }
    if (form.excerpt.length > 160) {
      setMsg(`Excerpt is ${form.excerpt.length} chars — must be 160 or less. Please shorten it.`);
      return;
    }
    if (form.metaDescription && form.metaDescription.length > 160) {
      setMsg(`Meta description is ${form.metaDescription.length} chars — must be 160 or less. Please shorten it.`);
      return;
    }
    if (form.metaTitle && form.metaTitle.length > 60) {
      setMsg(`Meta title is ${form.metaTitle.length} chars — must be 60 or less. Please shorten it.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        status: publishNow ? 'published' : form.status,
      };
      if (!payload.slug) {
        payload.slug = payload.title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
      }

      if (editId) {
        await axios.put(`/api/blog/admin/${editId}`, payload);
        setMsg('Post updated successfully.');
      } else {
        await axios.post('/api/blog/admin', payload);
        setMsg(publishNow ? 'Post published!' : 'Post saved as draft.');
      }
      await fetchBlogs();
      setView('list');
      setForm(emptyForm);
      setEditId(null);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to save post.');
    }
    setSaving(false);
  };

  const handleEdit = async (blog) => {
    setEditId(blog._id);
    setView('editor');
    setLoadingGenerate(true);
    try {
      // Fetch full blog data including content and excerpt
      const { data } = await axios.get(`/api/blog/admin/get/${blog._id}`);
      const full = data.blog;
      setForm({
        title: full.title || '',
        slug: full.slug || '',
        excerpt: full.excerpt || '',
        content: full.content || '',
        metaTitle: full.metaTitle || '',
        metaDescription: full.metaDescription || '',
        category: full.category || 'Clinical Research',
        tags: Array.isArray(full.tags) ? full.tags.join(', ') : '',
        author: full.author || 'BioMedArticles Editorial',
        status: full.status || 'draft',
        image: full.image || '',
        imageAlt: full.imageAlt || '',
        seoScore: full.seoScore || 0,
        aiGenerated: full.aiGenerated || false,
        featured: full.featured || false
      });
    } catch {
      setMsg('Failed to load post. Try again.');
    }
    setLoadingGenerate(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    await axios.delete(`/api/blog/admin/${id}`);
    await fetchBlogs();
    setMsg('Post deleted.');
  };

  const handleTogglePublish = async (id) => {
    await axios.put(`/api/blog/admin/${id}/publish`);
    await fetchBlogs();
  };

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const s = {
    page: { minHeight: 'calc(100vh - 56px)', background: 'var(--paper)' },
    header: { background: 'var(--ink)', color: 'var(--paper)', padding: '28px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    headerTitle: { fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--paper)' },
    btn: { background: 'var(--paper)', color: 'var(--ink)', border: 'none', padding: '9px 20px', fontSize: 13, fontWeight: 600, borderRadius: 0, cursor: 'pointer' },
    outlineBtn: { background: 'transparent', color: 'var(--paper)', border: '1.5px solid var(--paper)', padding: '9px 20px', fontSize: 13, borderRadius: 0, cursor: 'pointer' },
    body: { maxWidth: 1200, margin: '0 auto', padding: '32px 24px' },
    card: { background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '24px' },
    lbl: { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 6, fontFamily: 'var(--font-mono)' },
    inp: { width: '100%', padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14, outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', color: 'var(--ink)', background: 'var(--card)' },
    msgBox: { padding: '10px 14px', background: '#f0fdf4', color: '#15803d', borderRadius: 0, fontSize: 13, marginBottom: 16 },
  };

  // ── AI Topics View ──
  if (view === 'ai-topics') return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.headerTitle}>✨ AI Topic Suggestions</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={s.outlineBtn} onClick={() => setView('list')}>← Back</button>
          <button style={{ ...s.btn, background: 'var(--paper)', color: 'var(--ink)' }} onClick={handleSuggestTopics} disabled={loadingTopics}>
            {loadingTopics ? 'Generating...' : '↺ Refresh Topics'}
          </button>
        </div>
      </div>
      <div style={s.body}>
        {msg && <div style={s.msgBox}>{msg}</div>}
        <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>Click any topic to generate a full SEO-optimized blog post with AI.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {aiTopics.map((topic, i) => (
            <div key={i}
              onClick={() => handleGenerateFromTopic(topic)}
              style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '20px 22px', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.boxShadow = '6px 6px 0 rgba(10,10,10,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line-card)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{
                  background: topic.category === 'News' ? '#fff3cd' : topic.category === 'Regulatory' ? '#fdecea' : topic.category === 'Technology' ? '#e8f4fd' : topic.category === 'Drug Development' ? '#f3e8ff' : topic.category === 'Patient Advocacy' ? '#fce4ec' : topic.category === 'Public Health' ? '#e8f5e9' : '#f0fdf4',
                  color: topic.category === 'News' ? '#92610a' : topic.category === 'Regulatory' ? '#c0392b' : topic.category === 'Technology' ? '#1565c0' : topic.category === 'Drug Development' ? '#6b21a8' : topic.category === 'Patient Advocacy' ? '#ad1457' : topic.category === 'Public Health' ? '#2e7d32' : '#15803d',
                  fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)'
                }}>{topic.category}</span>
                <span style={{ background: '#ede9fe', color: '#7c3aed', fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>✨ AI</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, color: 'var(--ink)', marginBottom: 8 }}>{topic.title}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 12 }}>{topic.angle}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {(topic.keywords || []).map((kw, ki) => (
                  <span key={ki} style={{ background: 'var(--paper-2)', color: 'var(--muted)', fontSize: 11, padding: '2px 7px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>{kw}</span>
                ))}
              </div>
              <div style={{ marginTop: 14, fontSize: 12, color: 'var(--ink)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>Click to generate full post →</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Editor View (Manual + AI) ──
  if (view === 'editor' || view === 'ai-editor') return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>{editId ? 'Edit Post' : view === 'ai-editor' ? '✨ AI Generated Post' : 'New Post'}</div>
          {view === 'ai-editor' && !loadingGenerate && <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 4 }}>Review and edit before publishing</div>}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={s.outlineBtn} onClick={() => { setView('list'); setForm(emptyForm); setEditId(null); }}>← Back</button>
          <button style={{ ...s.outlineBtn }} onClick={() => handleSave(false)} disabled={saving}>{saving ? 'Saving...' : 'Save Draft'}</button>
          <button style={s.btn} onClick={() => handleSave(true)} disabled={saving}>{saving ? 'Publishing...' : '🚀 Publish Now'}</button>
        </div>
      </div>

      <div style={s.body}>
        {msg && <div style={s.msgBox}>{msg}</div>}

        {loadingGenerate ? (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <div className="spin" style={{ width: 40, height: 40, border: '3px solid var(--line-card)', borderTopColor: 'var(--ink)', borderRadius: '50%', margin: '0 auto 20px' }} />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8 }}>Generating your blog post...</div>
            <p style={{ color: 'var(--muted)', fontSize: 14 }}>Writing 1800-2000 words with SEO optimization. This takes about 15-20 seconds.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
            {/* Main editor */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={s.card}>
                <div style={{ marginBottom: 16 }}>
                  <label style={s.lbl}>Title *</label>
                  <input style={{ ...s.inp, fontSize: 18, fontWeight: 600 }} value={form.title} onChange={e => f('title', e.target.value)} placeholder="Post title..." />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={s.lbl}>Slug</label>
                  <input style={s.inp} value={form.slug} onChange={e => f('slug', e.target.value)} placeholder="url-friendly-slug (auto-generated)" />
                </div>
                <div>
                  <label style={s.lbl}>Excerpt * (150-160 chars)</label>
                  <div style={{ position: 'relative' }}>
                    <textarea style={{ ...s.inp, minHeight: 80, resize: 'vertical', borderColor: form.excerpt.length > 160 ? 'var(--danger)' : 'var(--line)' }} value={form.excerpt} onChange={e => f('excerpt', e.target.value)} placeholder="Brief description for SEO and card previews..." />
                    {form.excerpt.length > 160 && (
                      <button type="button" onClick={() => f('excerpt', form.excerpt.substring(0, 160))}
                        style={{ position: 'absolute', right: 8, bottom: 8, fontSize: 10, padding: '2px 8px', background: 'var(--danger)', color: 'var(--paper)', border: 'none', borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>
                        Auto-trim to 160
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: form.excerpt.length > 160 ? 'var(--danger)' : form.excerpt.length > 140 ? '#b45309' : 'var(--muted-2)', textAlign: 'right', marginTop: 4, fontFamily: 'var(--font-mono)', fontWeight: form.excerpt.length > 160 ? 700 : 400 }}>
                    {form.excerpt.length}/160 {form.excerpt.length > 160 ? `⚠ ${form.excerpt.length - 160} chars over limit` : ''}
                  </div>
                </div>
              </div>

              <div style={s.card}>
                <label style={s.lbl}>Content * (HTML supported)</label>
                <textarea
                  style={{ ...s.inp, minHeight: 500, resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.6 }}
                  value={form.content}
                  onChange={e => f('content', e.target.value)}
                  placeholder="<h2>Introduction</h2><p>Your content here...</p>"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>HTML supported · h2, h3, p, ul, ol, strong, a</div>
                  <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>~{Math.ceil(form.content.split(/\s+/).length / 200)} min read · {form.content.split(/\s+/).length} words</div>
                </div>
              </div>
            </div>

            {/* Sidebar settings */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={s.card}>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.lbl}>Status</label>
                  <select style={s.inp} value={form.status} onChange={e => f('status', e.target.value)}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.lbl}>Category</label>
                  <select style={s.inp} value={form.category} onChange={e => f('category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.lbl}>Author</label>
                  <input style={s.inp} value={form.author} onChange={e => f('author', e.target.value)} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.lbl}>Tags (comma separated)</label>
                  <input style={s.inp} value={form.tags} onChange={e => f('tags', e.target.value)} placeholder="clinical trials, phase 3, cancer..." />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="featured" checked={form.featured} onChange={e => f('featured', e.target.checked)} />
                  <label htmlFor="featured" style={{ fontSize: 13, cursor: 'pointer' }}>Mark as Featured</label>
                </div>
              </div>

              <div style={s.card}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 14 }}>SEO</div>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.lbl}>Meta Title (50-60 chars)</label>
                  <input style={s.inp} value={form.metaTitle} onChange={e => f('metaTitle', e.target.value)} />
                  <div style={{ fontSize: 11, color: form.metaTitle.length > 60 ? 'var(--danger)' : 'var(--muted-2)', textAlign: 'right', marginTop: 3, fontFamily: 'var(--font-mono)', fontWeight: form.metaTitle.length > 60 ? 700 : 400 }}>
                    {form.metaTitle.length}/60 {form.metaTitle.length > 60 ? `⚠ ${form.metaTitle.length - 60} over` : ''}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.lbl}>Meta Description (150-160 chars)</label>
                  <textarea style={{ ...s.inp, minHeight: 72, resize: 'vertical' }} value={form.metaDescription} onChange={e => f('metaDescription', e.target.value)} />
                  <div style={{ fontSize: 11, color: form.metaDescription.length > 160 ? 'var(--danger)' : form.metaDescription.length > 140 ? '#b45309' : 'var(--muted-2)', textAlign: 'right', marginTop: 3, fontFamily: 'var(--font-mono)', fontWeight: form.metaDescription.length > 160 ? 700 : 400 }}>
                    {form.metaDescription.length}/160 {form.metaDescription.length > 160 ? `⚠ ${form.metaDescription.length - 160} over` : ''}
                  </div>
                </div>
                {form.seoScore > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px', background: form.seoScore >= 90 ? '#f0fdf4' : '#fef9c3', borderRadius: 0 }}>
                    <span style={{ fontSize: 18 }}>{form.seoScore >= 90 ? '🟢' : '🟡'}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: form.seoScore >= 90 ? '#15803d' : '#854d0e' }}>SEO Score: {form.seoScore}/100</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{form.seoScore >= 90 ? 'Excellent — ready to rank!' : 'Good — consider more keywords'}</div>
                    </div>
                  </div>
                )}
              </div>

              <div style={s.card}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 14 }}>Image (Optional)</div>
                <div style={{ marginBottom: 10 }}>
                  <label style={s.lbl}>Image URL</label>
                  <input style={s.inp} value={form.image} onChange={e => f('image', e.target.value)} placeholder="https://..." />
                </div>
                <div>
                  <label style={s.lbl}>Image Alt Text</label>
                  <input style={s.inp} value={form.imageAlt} onChange={e => f('imageAlt', e.target.value)} placeholder="Descriptive alt text..." />
                </div>
                {form.image && (
                  <div style={{ marginTop: 12, borderRadius: 0, overflow: 'hidden', maxHeight: 140 }}>
                    <img src={form.image} alt="preview" style={{ width: '100%', height: 140, objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── List View ──
  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.headerTitle}>Blog Management</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            style={{ ...s.btn, background: 'var(--paper)', color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 7 }}
            onClick={handleSuggestTopics}
            disabled={loadingTopics}
          >
            {loadingTopics ? (
              <>
                <span className="spin" style={{ width: 14, height: 14, border: '2px solid rgba(10,10,10,0.2)', borderTopColor: 'var(--ink)', borderRadius: '50%', display: 'inline-block' }} />
                Getting topics...
              </>
            ) : '✨ AI Generate'}
          </button>
          <button style={s.btn} onClick={() => { setForm(emptyForm); setEditId(null); setView('editor'); }}>
            + New Post
          </button>
        </div>
      </div>

      <div style={s.body}>
        {msg && <div style={s.msgBox}>{msg}</div>}

        {/* Auto-Generation Panel */}
        <div style={{ ...s.card, marginBottom: 20, border: '2px solid var(--ink)', background: 'var(--paper-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>Auto Blog Generation</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {autoGenStatus?.enabled
                  ? `Generating ${autoGenStatus?.postsPerDay || 3} drafts daily at 6:00 AM ET`
                  : 'Disabled — enable to auto-generate daily blog drafts'}
                {autoGenStatus?.stats && (
                  <span style={{ marginLeft: 8, color: 'var(--muted-2)' }}>
                    · {autoGenStatus.stats.topicsRemaining} unique topics remaining of {autoGenStatus.stats.totalAvailableTopics} across {autoGenStatus.stats.categories} categories
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={autoGenCount}
                onChange={e => setAutoGenCount(Number(e.target.value))}
                style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 12, background: 'var(--card)', color: 'var(--ink)' }}
              >
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} post{n > 1 ? 's' : ''}</option>)}
              </select>
              <button
                onClick={handlePreviewTopics}
                disabled={autoGenLoading}
                style={{ padding: '6px 12px', border: '1.5px solid var(--ink)', background: 'transparent', borderRadius: 0, fontSize: 12, cursor: 'pointer', color: 'var(--ink)' }}
              >
                {autoGenLoading ? '...' : 'Preview Topics'}
              </button>
              <button
                onClick={handleAutoGenTrigger}
                disabled={autoGenTriggering}
                style={{ padding: '6px 14px', border: 'none', background: 'var(--ink)', color: 'var(--paper)', borderRadius: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                {autoGenTriggering ? 'Generating...' : 'Generate Now'}
              </button>
              <button
                onClick={handleToggleAutoCron}
                style={{
                  padding: '6px 12px', border: '1px solid', borderRadius: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  ...(autoGenStatus?.enabled
                    ? { borderColor: '#fecaca', background: 'var(--card)', color: 'var(--danger)' }
                    : { borderColor: '#a7f3d0', background: '#ecfdf5', color: '#059669' })
                }}
              >
                {autoGenStatus?.enabled ? 'Disable Daily' : 'Enable Daily'}
              </button>
            </div>
          </div>

          {autoGenStatus?.lastRun && (
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>
              Last run: {new Date(autoGenStatus.lastRun).toLocaleString()}
              {autoGenStatus.lastResult?.generated && ` · ${autoGenStatus.lastResult.generated.length} generated`}
              {autoGenStatus.lastResult?.failed?.length > 0 && ` · ${autoGenStatus.lastResult.failed.length} failed`}
              {autoGenStatus.lastResult?.elapsed && ` · ${autoGenStatus.lastResult.elapsed}`}
            </div>
          )}

          {previewTopics && (
            <div style={{ marginTop: 10, padding: 12, background: 'var(--card)', borderRadius: 0, border: '1px solid var(--line-card)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>
                Next {previewTopics.length} topics to generate
              </div>
              {previewTopics.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: i < previewTopics.length - 1 ? '1px solid var(--line-card)' : 'none' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--font-mono)', minWidth: 18 }}>{i + 1}.</span>
                  <span style={{ fontSize: 10, padding: '1px 6px', background: '#ede9fe', color: '#7c3aed', borderRadius: 0, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{t.category}</span>
                  <span style={{ fontSize: 10, padding: '1px 6px', background: '#f0fdf4', color: '#059669', borderRadius: 0, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{t.pillar}</span>
                  <span style={{ fontSize: 13 }}>{t.topic}</span>
                </div>
              ))}
              <button onClick={() => setPreviewTopics(null)} style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                Close preview
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--muted)' }}>
            {blogs.length} posts · {blogs.filter(b => b.status === 'published').length} published · {blogs.filter(b => b.status === 'draft').length} drafts
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div className="spin" style={{ width: 28, height: 28, border: '2px solid var(--line-card)', borderTopColor: 'var(--ink)', borderRadius: '50%', margin: '0 auto' }} />
          </div>
        ) : blogs.length === 0 ? (
          <div style={{ ...s.card, textAlign: 'center', padding: '60px 24px' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✍️</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8 }}>No posts yet</div>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>Click "AI Generate" to create your first SEO-optimized post in seconds.</p>
            <button style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '10px 24px', borderRadius: 0, fontSize: 14, fontWeight: 600, cursor: 'pointer' }} onClick={handleSuggestTopics}>
              ✨ Generate with AI
            </button>
          </div>
        ) : (
          <div style={s.card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--line-card)' }}>
                  {['Title', 'Category', 'Status', 'SEO', 'Views', 'Date', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blogs.map(blog => (
                  <tr key={blog._id} style={{ borderBottom: '1px solid var(--line-card)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--paper)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px', maxWidth: 300 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3 }}>{blog.title}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                        {blog.aiGenerated && <span style={{ background: '#ede9fe', color: '#7c3aed', fontSize: 10, padding: '1px 6px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>✨ AI</span>}
                        {blog.featured && <span style={{ background: '#fef9c3', color: '#854d0e', fontSize: 10, padding: '1px 6px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>★ Featured</span>}
                        <span style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>{blog.readTime}min</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{blog.category}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ ...STATUS_STYLE[blog.status], fontSize: 11, padding: '3px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                        {blog.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontFamily: 'var(--font-mono)', fontSize: 13, color: blog.seoScore >= 90 ? '#15803d' : blog.seoScore > 0 ? '#854d0e' : 'var(--muted-2)' }}>
                      {blog.seoScore > 0 ? `${blog.seoScore}` : '—'}
                    </td>
                    <td style={{ padding: '12px', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--muted)' }}>{blog.views || 0}</td>
                    <td style={{ padding: '12px', fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {blog.publishedAt ? new Date(blog.publishedAt).toLocaleDateString() : new Date(blog.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                        <button onClick={() => handleEdit(blog)} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>Edit</button>
                        <button onClick={() => handleTogglePublish(blog._id)} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', color: blog.status === 'published' ? 'var(--muted)' : '#15803d' }}>
                          {blog.status === 'published' ? 'Unpublish' : 'Publish'}
                        </button>
                        <button onClick={() => handleDelete(blog._id)} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #fecaca', background: 'var(--card)', borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', color: 'var(--danger)' }}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}